import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getToken } from '../auth/auth';

// ═══════════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════════

interface RowSeat {
    seat_id: string;
    num: string;
}

interface SectionRow {
    id: string;
    name?: string;
    row_name?: string;
    row_seats: RowSeat[];
}

interface Section {
    id: string;
    name: string;
    gate?: string;
    zone?: string;
    level?: string;
    capacity: number | '';
    category?: string;
    is_seat_specific?: boolean;
    section_rows: SectionRow[];
}

interface Ticket {
    id: number;
    title: string;
    description?: string;
    price: number;
    currency: string;
    old_price?: number;
    is_discount?: boolean;
    discount_amount?: number;
    current_amount: number; //not actual_amount
    max_amount: number;
    seat_sections: SeatMapEntry[];
    is_standing_block: boolean;
    is_seat_autoselect_block: boolean;
    max_simultaneous_purchase: number;
    allow_multiple_purchase: boolean;
    ticket_status: number;
    img_url?: string;
    booked_tickets?: string[];
}

interface SeatMapEntry {
    seats: string[];
    section_id: string;
}

interface EventData {
    id: number;
    title: string;
    start_date: string;
    end_date: string;
    place?: { name?: string };
    currency: string;
    seat_map_id: number | null;
    images?: string[];
    tickets: Ticket[];
}

interface VenueData {
    id: number;
    venue_name: string;
    capacity: number;
    svg_content?: string;
    svg_path?: string;
    image_preview_path?: string;
    sections: Section[];
}

interface SelectedSeat {
    ticketId?: number;
    seatId: string;
    rowId: string;
    sectionId: string;
    seatNum: string;
    rowName: string;
    sectionName: string;
    gate: string;
    zone: string;
    level: string;
}

// Seat colors
const SEAT_COLOR_AVAILABLE = '#87ceeb';
const SEAT_COLOR_SELECTED = '#2ecc71';
const SEAT_COLOR_BOOKED = '#6b6969';
const CHECKOUT_METHODS = new Set(['card', 'klarna', 'apple_pay', 'google_pay']);

function calcTicketFee(price: number): number {
    if (price < 25) return price * 0.15;
    if (price <= 50) return price * 0.10;
    return price * 0.05;
}

// Memoized SVG inner container — prevents React from replacing innerHTML on every parent re-render.
// Without this, any state change (seat click, dropdown toggle) causes React to re-set dangerouslySetInnerHTML,
// destroying DOM modifications (viewBox changes from pan/zoom, reordered seat groups).
const SvgInner = React.memo(({ html }: { html: string }) => (
    <div dangerouslySetInnerHTML={{ __html: html }} style={{ width: '100%', height: '100%' }} />
));

// ═══════════════════════════════════════════════════════════════════════════════
// Theme — dark/light follows system preference, defaults to dark
// ═══════════════════════════════════════════════════════════════════════════════

function useColorScheme(): boolean {
    const [isDark, setIsDark] = useState<boolean>(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return true;
    });
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return isDark;
}

const getCssVars = (dark: boolean): Record<string, string> => ({
    '--bg-page':             dark ? '#111111' : '#f0f4f8',
    '--bg-container':        dark ? '#1a1a1a' : '#ffffff',
    '--bg-svg':              dark ? '#0d0d0d' : '#1a1a1a',
    '--bg-surface':          dark ? '#1e1e1e' : '#f5f7fa',
    '--color-text':          dark ? '#eeeeee' : '#1a1a2e',
    '--color-text-sub':      dark ? '#cccccc' : '#444444',
    '--color-text-muted':    dark ? '#888888' : '#666666',
    '--color-text-faint':    dark ? '#555555' : '#aaaaaa',
    '--color-border':        dark ? '#333333' : '#e0e4ea',
    '--color-border-mid':    dark ? '#444444' : '#c0c8d4',
    '--shadow':              dark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
    '--color-spinner-track': dark ? '#333333' : '#dddddd',
});

// ═══════════════════════════════════════════════════════════════════════════════
// Pan/Zoom hook — viewBox-based, supports mouse wheel, drag-to-pan, touch pinch
// ═══════════════════════════════════════════════════════════════════════════════

interface ViewBox { x: number; y: number; w: number; h: number; }

function usePanZoom(containerRef: React.RefObject<HTMLDivElement | null>, enabled: boolean) {
    const vbRef = useRef<ViewBox>({ x: 0, y: 0, w: 800, h: 600 });
    const origVbRef = useRef<ViewBox>({ x: 0, y: 0, w: 800, h: 600 });
    const isPanningRef = useRef(false);
    const lastPosRef = useRef({ x: 0, y: 0 });
    const startPosRef = useRef({ x: 0, y: 0 });
    const lastPinchDistRef = useRef(0);
    const wasDragRef = useRef(false);

    const applyViewBox = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const svg = el.querySelector('svg');
        if (!svg) return;
        const vb = vbRef.current;
        svg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
    }, [containerRef]);

    const initFromSvg = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const svg = el.querySelector('svg');
        if (!svg) return;
        const vbAttr = svg.getAttribute('viewBox');
        if (vbAttr) {
            const [x, y, w, h] = vbAttr.split(/[\s,]+/).map(Number);
            if (!isNaN(w) && w > 0) {
                vbRef.current = { x, y, w, h };
                origVbRef.current = { x, y, w, h };
            }
        }
    }, [containerRef]);

    const screenToSvgScale = useCallback(() => {
        const el = containerRef.current;
        if (!el) return 1;
        const rect = el.getBoundingClientRect();
        return vbRef.current.w / (rect.width || 1);
    }, [containerRef]);

    const zoomAt = useCallback((clientX: number, clientY: number, factor: number) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vb = vbRef.current;
        const scale = screenToSvgScale();
        const svgX = vb.x + (clientX - rect.left) * scale;
        const svgY = vb.y + (clientY - rect.top) * scale;
        const newW = Math.max(10, Math.min(origVbRef.current.w * 10, vb.w * factor));
        const newH = Math.max(10, Math.min(origVbRef.current.h * 10, vb.h * factor));
        vbRef.current = {
            x: svgX - (svgX - vb.x) * (newW / vb.w),
            y: svgY - (svgY - vb.y) * (newH / vb.h),
            w: newW, h: newH,
        };
        applyViewBox();
    }, [containerRef, screenToSvgScale, applyViewBox]);

    const zoomToRect = useCallback((targetRect: { x: number; y: number; w: number; h: number }, padding = 40) => {
        const el = containerRef.current;
        if (!el) return;
        const containerRect = el.getBoundingClientRect();
        const aspect = containerRect.width / (containerRect.height || 1);
        let w = targetRect.w + padding * 2;
        let h = targetRect.h + padding * 2;
        if (w / h > aspect) { h = w / aspect; } else { w = h * aspect; }
        vbRef.current = {
            x: targetRect.x + targetRect.w / 2 - w / 2,
            y: targetRect.y + targetRect.h / 2 - h / 2,
            w, h,
        };
        applyViewBox();
    }, [containerRef, applyViewBox]);

    const resetZoom = useCallback(() => {
        vbRef.current = { ...origVbRef.current };
        applyViewBox();
    }, [applyViewBox]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el || !enabled) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const factor = e.deltaY > 0 ? 1.12 : 0.89;
            zoomAt(e.clientX, e.clientY, factor);
        };

        const onPointerDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            isPanningRef.current = true;
            wasDragRef.current = false;
            startPosRef.current = { x: e.clientX, y: e.clientY };
            lastPosRef.current = { x: e.clientX, y: e.clientY };
            el.setPointerCapture(e.pointerId);
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!isPanningRef.current) return;
            const sc = screenToSvgScale();
            const dx = (e.clientX - lastPosRef.current.x) * sc;
            const dy = (e.clientY - lastPosRef.current.y) * sc;
            vbRef.current.x -= dx;
            vbRef.current.y -= dy;
            lastPosRef.current = { x: e.clientX, y: e.clientY };
            const totalDx = e.clientX - startPosRef.current.x;
            const totalDy = e.clientY - startPosRef.current.y;
            if (Math.hypot(totalDx, totalDy) > 5) wasDragRef.current = true;
            applyViewBox();
        };

        const onPointerUp = (e: PointerEvent) => {
            isPanningRef.current = false;
            el.releasePointerCapture(e.pointerId);
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                const tdx = e.touches[0].clientX - e.touches[1].clientX;
                const tdy = e.touches[0].clientY - e.touches[1].clientY;
                lastPinchDistRef.current = Math.hypot(tdx, tdy);
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const tdx = e.touches[0].clientX - e.touches[1].clientX;
                const tdy = e.touches[0].clientY - e.touches[1].clientY;
                const dist = Math.hypot(tdx, tdy);
                if (lastPinchDistRef.current > 0) {
                    const f = lastPinchDistRef.current / dist;
                    const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
                    const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
                    zoomAt(cx, cy, f);
                }
                lastPinchDistRef.current = dist;
            }
        };

        const onTouchEnd = () => { lastPinchDistRef.current = 0; };

        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('pointerdown', onPointerDown);
        el.addEventListener('pointermove', onPointerMove);
        el.addEventListener('pointerup', onPointerUp);
        el.addEventListener('pointercancel', onPointerUp);
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);

        return () => {
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('pointerdown', onPointerDown);
            el.removeEventListener('pointermove', onPointerMove);
            el.removeEventListener('pointerup', onPointerUp);
            el.removeEventListener('pointercancel', onPointerUp);
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [enabled, containerRef, zoomAt, screenToSvgScale, applyViewBox]);

    return useMemo(
        () => ({ initFromSvg, zoomToRect, resetZoom, applyViewBox, wasDragRef }),
        [initFromSvg, zoomToRect, resetZoom, applyViewBox]
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════════

const SeatSelectionComponent: React.FC = () => {
    const appUrl = 'https://maineventbackend.seuytel.com/api/';
    const storageUrl = 'https://maineventbackend.seuytel.com/storage/';
    const isDark = useColorScheme();

    // ── Get URL params ──
    const [searchParams] = useSearchParams();
    const eventId = searchParams.get('eventId');
    const ticketIdParam = searchParams.get('ticketId');
    const selectedSeatsParam = searchParams.get('selectedSeats');
    const quantityParam = searchParams.get('quantity');
    const mode = (searchParams.get('mode') || 'select') as 'select' | 'view';
    const browserCheckoutMode = searchParams.get('checkout') === '1';
    const requestedPaymentMethodType = searchParams.get('payment_method_type') || '';
    const checkoutPaymentMethodType = CHECKOUT_METHODS.has(requestedPaymentMethodType)
        ? requestedPaymentMethodType
        : '';
    // Read-only mode (resale tickets open the map with ?view_only=true): the buyer
    // can pan/zoom to SEE their seat but must not pick a different one, so every SVG
    // selection handler is detached and the seats lose their click affordance.
    const viewOnly = mode === 'view' || searchParams.get('view_only') === 'true';

    // ── Core data ──
    const [event, setEvent] = useState<EventData | null>(null);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [venue, setVenue] = useState<VenueData | null>(null);
    const [svgContent, setSvgContent] = useState('');
    const [bookedSeatIds, setBookedSeatIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ── Selection state ──
    const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
    const selectedSectionIdRef = useRef<string | null>(null);
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [selectedSeats, setSelectedSeats] = useState<SelectedSeat[]>([]);
    const [ticketQuantity, setTicketQuantity] = useState(1);
    const [seatWarningVisible, setSeatWarningVisible] = useState(false);
    const seatWarningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const anchorSeatRef = useRef<string | null>(null);
    const [selectedTicketsDropdownOpen, setSelectedTicketsDropdownOpen] = useState(false);

    // ── Pre-selection tracking ──
    const preSelectionDoneRef = useRef(false);
    // Counts how many renders to skip in the anchor-adjustment effect after
    // pre-selection. A single boolean is insufficient because the clamp effect
    // (ticketQuantity > maxQuantity) can trigger a SECOND render, recreating
    // selectAroundAnchor and firing the anchor-adjustment again after the flag
    // was already cleared on the first pass.
    const skipAnchorAdjustCountRef = useRef(0);

    // ── Venue preview ──
    const [venuePreviewOpen, setVenuePreviewOpen] = useState(false);

    // ── Venue preview ref ──
    const venueThumbRef = useRef<HTMLImageElement>(null);

    // ── Refs ──
    const overviewRef = useRef<HTMLDivElement>(null);
    const detailRef = useRef<HTMLDivElement>(null);

    // ── Layout state for responsive ──
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const isLargeScreen = windowWidth >= 1024;

    // ── Pan/Zoom hooks ──
    const overviewPZ = usePanZoom(overviewRef, !!svgContent);
    const detailPZ = usePanZoom(detailRef, !!svgContent && !!selectedSectionId);

    // ── Row name lookup: resolve display name from venue section_rows by row id ──
    const rowNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const section of (venue?.sections || [])) {
            for (const row of (section.section_rows || [])) {
                // row.name is the display label ("1", "2", …); row.id is the internal ID
                map.set(row.id, row.name || row.row_name || row.id);
            }
        }
        return map;
    }, [venue]);

    // If a value looks like a row ID (contains underscores), extract the last segment
    // e.g. "sh-1773133727059-r-1" → IDs don't use "_" but real IDs use "-";
    // the convention here is that display names never contain "-r-",
    // so split on "-r-" and take the last part if it matches that pattern.
    const resolveRowName = useCallback((rawName: string, rowId?: string): string => {
        // First try the lookup map if we have the row ID
        if (rowId) {
            const mapped = rowNameById.get(rowId);
            if (mapped && mapped !== rowId) return mapped;
        }
        // If rawName looks like an internal ID (matches the pattern *-r-*), extract the last segment
        if (rawName && rawName.includes('-r-')) {
            const parts = rawName.split('-r-');
            return parts[parts.length - 1];
        }
        return rawName;
    }, [rowNameById]);

    // Keep ref in sync for use in native event handlers
    useEffect(() => {
        selectedSectionIdRef.current = selectedSectionId;
    }, [selectedSectionId]);

    // ═════════════════════════════════════════════════════════════════════════
    // Data Loading
    // ═════════════════════════════════════════════════════════════════════════

    useEffect(() => {
        if (!eventId) {
            setError('No event ID provided. Use ?eventId=<id> in the URL.');
            setLoading(false);
            return;
        }
        loadEventData(eventId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    const loadEventData = async (id: string) => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${appUrl}seatmap/eventData/${encodeURIComponent(id)}`);
            if (!res.ok) throw new Error('Failed to load event data');
            const json = await res.json();
            if (json.status !== 'success') throw new Error(json.message || 'Load failed');

            const data = json.data;
            const eventData: EventData = data;
            setEvent(eventData);
            setTickets(data.tickets || []);
            const allBookedSeats = new Set<string>(data.booked_seats || []);
            (data.tickets || []).forEach((t: any) => {
                if (Array.isArray(t.booked_tickets)) {
                    t.booked_tickets.forEach((sid: string) => allBookedSeats.add(sid));
                }
            });
            setBookedSeatIds(allBookedSeats);

            if (data.venue) {
                const venueData: VenueData = data.venue;
                setVenue(venueData);
                if (venueData.svg_content) {
                    setSvgContent(venueData.svg_content);
                }
            }
        } catch (err: any) {
            setError(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    // ═════════════════════════════════════════════════════════════════════════
    // Pre-selection: apply URL params after data loads
    // ═════════════════════════════════════════════════════════════════════════

    useEffect(() => {
        if (preSelectionDoneRef.current || !event || !tickets.length || loading) return;

        let targetSectionId: string | null = null;
        let targetTicket: Ticket | null = null;
        let preSeats: SelectedSeat[] = [];

        // Auto-select ticket if ticketId provided
        if (ticketIdParam) {
            targetTicket = tickets.find(t => t.id === Number(ticketIdParam)) || null;
            if (targetTicket?.seat_sections?.length) {
                targetSectionId = targetTicket.seat_sections[0].section_id;
            }
        }

        // Parse pre-selected seats if provided
        if (selectedSeatsParam) {
            try {
                const parsed = JSON.parse(decodeURIComponent(selectedSeatsParam));
                if (Array.isArray(parsed) && parsed.length > 0) {
                    preSeats = parsed.map((s: any) => ({
                        ticketId: s.ticket_id,
                        seatId: s.seat_id,
                        rowId: s.row_id,
                        sectionId: s.section_id,
                        seatNum: s.seat_num,
                        // Resolve display name from venue data; fall back to extracting last ID segment
                        rowName: resolveRowName(rowNameById.get(s.row_id) || s.row_name || s.row_id || '', s.row_id),
                        sectionName: s.section_name,
                        gate: s.gate || '',
                        zone: s.zone || '',
                        level: s.level || '',
                    }));
                    if (!targetSectionId && preSeats[0]?.sectionId) {
                        targetSectionId = preSeats[0].sectionId;
                    }
                }
            } catch (e) {
                console.error('Failed to parse selectedSeats param:', e);
            }
        }

        if (targetSectionId) setSelectedSectionId(targetSectionId);
        if (targetTicket) setSelectedTicket(targetTicket);
        if (preSeats.length > 0) {
            // The caller is re-opening the map with seats it already holds (edit
            // flow). Those seats may appear in booked_seats — remove them so they
            // paint as SELECTED (not booked/grey) and stay clickable; otherwise
            // only seats missing from booked_seats would show as selected.
            const ownIds = new Set(preSeats.map(s => s.seatId).filter(Boolean));
            setBookedSeatIds(prev => {
                if (![...ownIds].some(id => prev.has(id))) return prev;
                return new Set([...prev].filter(id => !ownIds.has(id)));
            });
            // Skip the next 3 anchor-adjustment firings (initial render +
            // potential clamp re-render + potential auto-select-ticket re-render).
            skipAnchorAdjustCountRef.current = 3;
            setSelectedSeats(preSeats);
            setTicketQuantity(preSeats.length);
            anchorSeatRef.current = preSeats[0].seatId;
        } else if (quantityParam) {
            // Standing-block restore: no seat IDs, just the quantity
            const qty = parseInt(quantityParam) || 1;
            if (qty > 1) setTicketQuantity(qty);
        }

        preSelectionDoneRef.current = true;
    }, [event, tickets, loading, ticketIdParam, selectedSeatsParam, quantityParam, rowNameById, resolveRowName]);

    // ═════════════════════════════════════════════════════════════════════════
    // Derived data
    // ═════════════════════════════════════════════════════════════════════════

    const sections: Section[] = venue?.sections || [];

    // Find the selected section object
    const selectedSection = useMemo(
        () => sections.find(s => s.id === selectedSectionId) || null,
        [sections, selectedSectionId]
    );

    // Tickets that apply to the selected section
    const ticketsForSection = useMemo(() => {
        if (!selectedSectionId) return [];
        return tickets.filter(t =>
            t.ticket_status === 1 &&
            Array.isArray(t.seat_sections) &&
            t.seat_sections.some(entry => entry.section_id === selectedSectionId)
        );
    }, [tickets, selectedSectionId]);

    // Auto-select first ticket when section changes
    useEffect(() => {
        if (ticketsForSection.length > 0 && !ticketsForSection.find(t => t.id === selectedTicket?.id)) {
            setSelectedTicket(ticketsForSection[0]);
        } else if (ticketsForSection.length === 0) {
            setSelectedTicket(null);
        }
    }, [ticketsForSection, selectedTicket?.id]);

    // Total available seats in the selected section
    const sectionTotalSeats = useMemo(() => {
        if (!selectedSection) return 0;
        return (selectedSection.section_rows || []).reduce(
            (sum, row) => sum + (row.row_seats?.length || 0), 0
        );
    }, [selectedSection]);

    // Booked seats count in the selected section
    const sectionBookedCount = useMemo(() => {
        if (!selectedSection) return 0;
        let count = 0;
        for (const row of (selectedSection.section_rows || [])) {
            for (const seat of (row.row_seats || [])) {
                if (bookedSeatIds.has(seat.seat_id)) count++;
            }
        }
        return count;
    }, [selectedSection, bookedSeatIds]);

    // Remaining capacity for the selected ticket
    const ticketRemaining = useMemo(() => {
        if (!selectedTicket) return 0;
        return Math.max(0, selectedTicket.max_amount - selectedTicket.current_amount);
    }, [selectedTicket]);

    // Max quantity user can select
    const maxQuantity = useMemo(() => {
        if (!selectedTicket) return 1;
        const remaining = selectedTicket.max_amount - selectedTicket.current_amount;
        const maxSimul = selectedTicket.max_simultaneous_purchase || 10;
        // For non-standing blocks, also cap by available (non-booked) seats in section
        if (!selectedTicket.is_standing_block && selectedSection) {
            const availableInSection = sectionTotalSeats - sectionBookedCount;
            return Math.max(1, Math.min(remaining, maxSimul, availableInSection));
        }
        return Math.max(1, Math.min(remaining, maxSimul));
    }, [selectedTicket, selectedSection, sectionTotalSeats, sectionBookedCount]);

    // Clamp quantity when max changes — but not during the pre-selection
    // stabilisation window, otherwise the pre-selected count gets reduced
    // before the user even sees the map.
    useEffect(() => {
        if (skipAnchorAdjustCountRef.current > 0) return;
        if (ticketQuantity > maxQuantity) setTicketQuantity(Math.max(1, maxQuantity));
    }, [maxQuantity, ticketQuantity]);

    // Venue preview URL
    const venuePreviewUrl = useMemo(() => {
        if (!venue?.image_preview_path) return '';
        const p = venue.image_preview_path;
        return p.startsWith('http') ? p : `${storageUrl}${p}`;
    }, [venue, storageUrl]);

    // Stop pointer events on the venue preview thumbnail from being captured by pan-zoom.
    // Native listeners are needed because usePanZoom uses native addEventListener,
    // and React's synthetic stopPropagation doesn't prevent native listeners on ancestor elements.
    useEffect(() => {
        const thumb = venueThumbRef.current;
        if (!thumb) return;
        const stop = (e: PointerEvent) => e.stopPropagation();
        thumb.addEventListener('pointerdown', stop);
        thumb.addEventListener('pointermove', stop);
        thumb.addEventListener('pointerup', stop);
        return () => {
            thumb.removeEventListener('pointerdown', stop);
            thumb.removeEventListener('pointermove', stop);
            thumb.removeEventListener('pointerup', stop);
        };
    }, [venuePreviewUrl]);

    // ═════════════════════════════════════════════════════════════════════════
    // Click-based seat selection: select N consecutive seats around clicked seat
    // Smart algorithm: avoids leaving single isolated (orphan) seats
    // ═════════════════════════════════════════════════════════════════════════

    const selectAroundAnchor = useCallback((anchorSeatId: string): boolean => {
        if (!selectedSection) return false;

        const quantity = ticketQuantity;

        for (const row of (selectedSection.section_rows || [])) {
            const seats = row.row_seats || [];
            const anchorIdx = seats.findIndex(s => s.seat_id === anchorSeatId);
            if (anchorIdx === -1) continue;

            const available = seats.map(s => !bookedSeatIds.has(s.seat_id));

            // Find the island of consecutive available seats containing the anchor
            let islandStart = anchorIdx;
            while (islandStart > 0 && available[islandStart - 1]) islandStart--;
            let islandEnd = anchorIdx + 1;
            while (islandEnd < seats.length && available[islandEnd]) islandEnd++;

            const islandLen = islandEnd - islandStart;
            if (islandLen < quantity) return false;

            // Find all valid start positions that include the anchor seat
            const candidates: number[] = [];
            for (let start = Math.max(islandStart, anchorIdx - quantity + 1);
                 start <= Math.min(anchorIdx, islandEnd - quantity);
                 start++) {
                const leftRemaining = start - islandStart;
                const rightRemaining = islandEnd - (start + quantity);
                if (leftRemaining === 1 || rightRemaining === 1) continue;
                candidates.push(start);
            }

            if (candidates.length === 0) return false;

            // Pick the position closest to centering on the anchor
            candidates.sort((a, b) =>
                Math.abs(a + (quantity - 1) / 2 - anchorIdx) - Math.abs(b + (quantity - 1) / 2 - anchorIdx)
            );

            const bestStart = candidates[0];
            const selected = seats.slice(bestStart, bestStart + quantity).map(s => ({
                seatId: s.seat_id,
                rowId: row.id,
                sectionId: selectedSection.id,
                seatNum: s.num,
                // row.name is the display label from the API; fall back via lookup map then ID extraction
                rowName: resolveRowName(row.name || rowNameById.get(row.id) || row.row_name || row.id, row.id),
                sectionName: selectedSection.name,
                gate: selectedSection.gate || '',
                zone: selectedSection.zone || '',
                level: selectedSection.level || '',
            }));
            setSelectedSeats(selected);
            setSeatWarningVisible(false);
            return true;
        }
        return false;
    }, [selectedSection, ticketQuantity, bookedSeatIds, rowNameById, resolveRowName]);

    // Show warning popup helper
    const showSeatWarning = useCallback(() => {
        setSelectedSeats([]);
        setSeatWarningVisible(true);
        if (seatWarningTimerRef.current) clearTimeout(seatWarningTimerRef.current);
        seatWarningTimerRef.current = setTimeout(() => setSeatWarningVisible(false), 3000);
    }, []);

    // Re-adjust selection when quantity changes (only if user already clicked a seat)
    useEffect(() => {
        // Skip after pre-selection: the counter covers the initial render plus
        // any cascading re-renders caused by the clamp or auto-select effects.
        if (skipAnchorAdjustCountRef.current > 0) {
            skipAnchorAdjustCountRef.current--;
            return;
        }
        // Quantity dropped to 0 → the user is clearing their selection. Drop every
        // seat and the anchor so no "couldn't fit N seats" warning fires; the
        // footer then switches to a "Remove Selection" confirm (see below).
        if (ticketQuantity <= 0) {
            setSelectedSeats([]);
            anchorSeatRef.current = null;
            return;
        }
        if (!selectedTicket || selectedTicket.is_standing_block || !selectedSection) return;
        const anchor = anchorSeatRef.current;
        if (!anchor) return;

        // Verify anchor still belongs to current section
        const anchorExists = (selectedSection.section_rows || []).some(row =>
            (row.row_seats || []).some(s => s.seat_id === anchor)
        );
        if (!anchorExists) {
            anchorSeatRef.current = null;
            return;
        }

        if (!selectAroundAnchor(anchor)) {
            showSeatWarning();
        }
    }, [selectedTicket, selectedSection, ticketQuantity, bookedSeatIds, selectAroundAnchor, showSeatWarning]);

    // ═════════════════════════════════════════════════════════════════════════
    // SVG interaction – Detail: click a seat to select N consecutive around it
    // ═════════════════════════════════════════════════════════════════════════

    const handleDetailSeatClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (detailPZ.wasDragRef.current) return;
        const target = document.elementFromPoint(e.clientX, e.clientY);
        if (!target) return;
        if (!target.classList.contains('seat')) return;

        const seatId = target.getAttribute('data-seat-id') || '';
        const sectionId = target.getAttribute('data-section-id') || '';
        if (sectionId !== selectedSectionId) return;
        if (bookedSeatIds.has(seatId)) return;
        if (selectedTicket?.is_standing_block) return;

        anchorSeatRef.current = seatId;

        if (!selectAroundAnchor(seatId)) {
            showSeatWarning();
        }
    }, [selectedSectionId, bookedSeatIds, selectedTicket, selectAroundAnchor, showSeatWarning]);

    // ═════════════════════════════════════════════════════════════════════════
    // SVG interaction – Overview: click section blocks only
    // FIX: Use onPointerUp with wasDrag check instead of onClick,
    // to prevent the pan/zoom pointer capture from swallowing the event.
    // ═════════════════════════════════════════════════════════════════════════

    const handleOverviewPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        // Capture and immediately reset the drag flag so it can never be left in a
        // stale true-state that permanently blocks subsequent block-selection clicks.
        const wasDrag = overviewPZ.wasDragRef.current;
        overviewPZ.wasDragRef.current = false;
        if (wasDrag) return;

        // setPointerCapture() redirects e.target to the capturing element (the container div),
        // so we must use elementFromPoint to find the actual element under the cursor.
        const target = document.elementFromPoint(e.clientX, e.clientY);
        if (!target) return;
        const seatBlock = target.closest('.seat-block') || (target.classList.contains('seat-block') ? target : null);
        if (seatBlock) {
            const sectionId = seatBlock.getAttribute('data-section-id');
            if (sectionId) {
                // Always update the selected section so switching blocks always works.
                // Only clear seat/quantity state when the section actually changes.
                setSelectedSectionId(sectionId);
                if (sectionId !== selectedSectionIdRef.current) {
                    setSelectedSeats([]);
                    setTicketQuantity(1);
                    anchorSeatRef.current = null;
                }
            }
        }
    }, []);

    // ═════════════════════════════════════════════════════════════════════════
    // Build overview SVG (sections clickable, seats non-interactive)
    // Only rebuilds when raw SVG content changes — all dynamic styling via DOM
    // ═════════════════════════════════════════════════════════════════════════

    const overviewSvgHtml = useMemo(() => {
        if (!svgContent) return '';

        const styles = `
            .seat-block { cursor: ${viewOnly ? 'default' : 'pointer'}; pointer-events: ${viewOnly ? 'none' : 'auto'} !important; transition: filter 0.15s; }
            ${viewOnly ? '' : '.seat-block:hover { filter: brightness(1.2) drop-shadow(0 0 6px rgba(255,255,255,0.3)); }'}
            .seat { pointer-events: none; }
            .row-label { pointer-events: none; }
        `;
        let result = svgContent;
        result = result.replace(/<svg([^>]*)>/, (_match, attrs) => {
            let newAttrs = attrs;
            if (!attrs.includes('width=')) newAttrs += ' width="100%" height="100%"';
            return `<svg${newAttrs}>`;
        });
        if (result.includes('<style>')) {
            return result.replace(/<style>/, `<style>${styles}`);
        }
        return result.replace(/<svg([^>]*)>/, `<svg$1><style>${styles}</style>`);
    }, [svgContent, viewOnly]);

    // Apply section highlight + seat colors on overview via DOM manipulation
    useEffect(() => {
        const el = overviewRef.current;
        if (!el) return;
        const svg = el.querySelector('svg');
        if (!svg) return;

        // Highlight all sections belonging to the selected ticket (multi-block support).
        // Blocks from the same ticket as the clicked one are all highlighted;
        // all other blocks remain at normal opacity and fully clickable.
        const activeSectionIds = new Set<string>(
            selectedTicket?.seat_sections?.map(e => e.section_id) ||
            (selectedSectionId ? [selectedSectionId] : [])
        );

        // Section highlight — never touch pointer-events so all blocks stay clickable
        const allBlocks = svg.querySelectorAll('.seat-block');
        allBlocks.forEach(block => {
            const el = block as SVGElement;
            const sid = block.getAttribute('data-section-id');
            el.style.pointerEvents = 'auto';   // always keep clickable
            if (sid && activeSectionIds.has(sid)) {
                el.style.filter = 'brightness(1.3)';
                el.style.stroke = '#3498db';
                el.style.strokeWidth = '3';
            } else {
                el.style.filter = '';
                el.style.stroke = '';
                el.style.strokeWidth = '';
            }
        });

        // Seat colors
        const allSeats = svg.querySelectorAll('.seat');
        const selectedIds = new Set(selectedSeats.map(s => s.seatId));

        allSeats.forEach(seat => {
            const sid = seat.getAttribute('data-seat-id') || '';
            if (bookedSeatIds.has(sid)) {
                (seat as SVGElement).style.fill = SEAT_COLOR_BOOKED;
                (seat as SVGElement).style.opacity = '0.7';
            } else if (selectedIds.has(sid)) {
                (seat as SVGElement).style.fill = SEAT_COLOR_SELECTED;
                (seat as SVGElement).style.opacity = '1';
                (seat as SVGElement).style.stroke = '#27ae60';
                (seat as SVGElement).style.strokeWidth = '2';
            } else {
                (seat as SVGElement).style.fill = '';
                (seat as SVGElement).style.opacity = '';
                (seat as SVGElement).style.stroke = '';
                (seat as SVGElement).style.strokeWidth = '';
            }
        });
    }, [selectedSeats, bookedSeatIds, selectedSectionId, selectedTicket, overviewSvgHtml]);

    // ═════════════════════════════════════════════════════════════════════════
    // Build detail SVG — full SVG, zoomed to selected section
    // The SVG structure only changes when section changes (not when seats change)
    // Seat coloring is applied via DOM manipulation to avoid zoom reset
    // ═════════════════════════════════════════════════════════════════════════

    const detailSvgHtml = useMemo(() => {
        if (!svgContent || !selectedSectionId) return '';

        const styles = `
            .seat-block { opacity: 0.1; pointer-events: none; }
            .seat-block[data-section-id="${CSS.escape(selectedSectionId)}"] { opacity: 1 !important; stroke: #3498db; stroke-width: 2; filter: brightness(1.15); pointer-events: none; }
            .seat { opacity: 0.08; pointer-events: none; }
            .seat[data-section-id="${CSS.escape(selectedSectionId)}"] { opacity: 1 !important; cursor: ${viewOnly ? 'default' : 'pointer'}; pointer-events: ${viewOnly ? 'none' : 'auto'}; transition: fill 0.1s; }
            ${viewOnly ? '' : `.seat[data-section-id="${CSS.escape(selectedSectionId)}"]:hover { filter: brightness(1.3); stroke: #fff; stroke-width: 2; }`}
            .row-label { opacity: 0.08; pointer-events: none; }
            image { opacity: 0.15; }
            g:has(> .seat[data-section-id="${CSS.escape(selectedSectionId)}"]) > .row-label { opacity: 1 !important; }
        `;

        let result = svgContent;
        result = result.replace(/<svg([^>]*)>/, (_match, attrs) => {
            let newAttrs = attrs;
            if (!attrs.includes('width=')) newAttrs += ' width="100%" height="100%"';
            return `<svg${newAttrs}>`;
        });
        if (result.includes('<style>')) {
            return result.replace(/<style>[\s\S]*?<\/style>/, `<style>${styles}</style>`);
        }
        return result.replace(/<svg([^>]*)>/, `<svg$1><style>${styles}</style>`);
    }, [svgContent, selectedSectionId, viewOnly]);

    // Apply seat colors (booked/selected) via DOM manipulation — avoids rebuilding SVG HTML
    // Also ensure seat-row groups stay on top of other shapes (seat-block fills, etc.)
    useEffect(() => {
        const el = detailRef.current;
        if (!el || !selectedSectionId) return;
        const svg = el.querySelector('svg');
        if (!svg) return;

        // Forcibly disable pointer events on every seat-block — CSS alone can be
        // overridden by inline attributes on the SVG elements themselves.
        svg.querySelectorAll('.seat-block').forEach(block => {
            (block as SVGElement).style.pointerEvents = 'none';
            (block as SVGElement).style.cursor = 'default';
        });

        // Bring seat-row groups (containing seats of the selected section) to the top
        // so they render above other shapes like seat-block fills
        const seatGroups = svg.querySelectorAll(`g:has(> .seat[data-section-id="${CSS.escape(selectedSectionId)}"])`);
        seatGroups.forEach(g => {
            g.parentElement?.appendChild(g);
        });

        const allSeats = svg.querySelectorAll(`.seat[data-section-id="${CSS.escape(selectedSectionId)}"]`);
        const selectedIds = new Set(selectedSeats.map(s => s.seatId));

        allSeats.forEach(seat => {
            const sid = seat.getAttribute('data-seat-id') || '';
            if (bookedSeatIds.has(sid)) {
                (seat as SVGElement).style.fill = SEAT_COLOR_BOOKED;
                (seat as SVGElement).style.opacity = '0.7';
                (seat as SVGElement).style.pointerEvents = 'none';
                (seat as SVGElement).style.cursor = 'default';
                (seat as SVGElement).style.stroke = '';
                (seat as SVGElement).style.strokeWidth = '';
            } else if (selectedIds.has(sid)) {
                (seat as SVGElement).style.fill = SEAT_COLOR_SELECTED;
                (seat as SVGElement).style.opacity = '1';
                (seat as SVGElement).style.stroke = '#27ae60';
                (seat as SVGElement).style.strokeWidth = '2';
            } else {
                (seat as SVGElement).style.fill = '';
                (seat as SVGElement).style.opacity = '';
                (seat as SVGElement).style.stroke = '';
                (seat as SVGElement).style.strokeWidth = '';
                (seat as SVGElement).style.pointerEvents = '';
                (seat as SVGElement).style.cursor = '';
            }
        });
    }, [selectedSeats, bookedSeatIds, selectedSectionId, detailSvgHtml]);

    // ═════════════════════════════════════════════════════════════════════════
    // Compute bounding box of a section from the SVG DOM
    // ═════════════════════════════════════════════════════════════════════════

    const getSectionBBox = useCallback((sectionId: string, svgRoot: Element): { x: number; y: number; w: number; h: number } | null => {
        const svg = svgRoot as SVGSVGElement;
        const block = svg.querySelector(`.seat-block[data-section-id="${CSS.escape(sectionId)}"]`) as SVGGraphicsElement | null;
        const seats = svg.querySelectorAll(`.seat[data-section-id="${CSS.escape(sectionId)}"]`);

        if (!block && seats.length === 0) return null;

        const svgScreenCTM = svg.getScreenCTM();
        if (!svgScreenCTM) return null;
        const invSvgCTM = svgScreenCTM.inverse();

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        const expandElement = (el: SVGGraphicsElement) => {
            try {
                const bbox = el.getBBox();
                if (bbox.width === 0 && bbox.height === 0) return;
                const elCTM = el.getScreenCTM();
                if (!elCTM) return;
                const m = invSvgCTM.multiply(elCTM);
                const corners = [
                    [bbox.x, bbox.y],
                    [bbox.x + bbox.width, bbox.y],
                    [bbox.x, bbox.y + bbox.height],
                    [bbox.x + bbox.width, bbox.y + bbox.height],
                ];
                for (const [cx, cy] of corners) {
                    const pt = svg.createSVGPoint();
                    pt.x = cx; pt.y = cy;
                    const tp = pt.matrixTransform(m);
                    minX = Math.min(minX, tp.x);
                    minY = Math.min(minY, tp.y);
                    maxX = Math.max(maxX, tp.x);
                    maxY = Math.max(maxY, tp.y);
                }
            } catch { /* getBBox can throw if element has no geometry */ }
        };

        if (block) expandElement(block);

        const parentGs = new Set<SVGGElement>();
        seats.forEach(s => {
            const parent = s.parentElement;
            if (parent && parent.tagName.toLowerCase() === 'g') {
                parentGs.add(parent as unknown as SVGGElement);
            } else {
                expandElement(s as SVGGraphicsElement);
            }
        });
        parentGs.forEach(g => expandElement(g));

        if (minX === Infinity) return null;
        return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }, []);

    // ═════════════════════════════════════════════════════════════════════════
    // Initialize pan/zoom when SVG changes
    // ═════════════════════════════════════════════════════════════════════════

    useEffect(() => {
        const t = setTimeout(() => overviewPZ.initFromSvg(), 50);
        return () => clearTimeout(t);
    }, [overviewSvgHtml, overviewPZ]);

    // When detail SVG changes, init and zoom to selected section
    useEffect(() => {
        if (!detailSvgHtml || !selectedSectionId) return;
        const t = setTimeout(() => {
            detailPZ.initFromSvg();
            const el = detailRef.current;
            if (!el) return;
            const svg = el.querySelector('svg');
            if (!svg) return;

            const bbox = getSectionBBox(selectedSectionId, svg);
            if (bbox) detailPZ.zoomToRect(bbox, 30);
        }, 50);
        return () => clearTimeout(t);
    }, [detailSvgHtml, selectedSectionId, detailPZ, getSectionBBox]);

    // ═════════════════════════════════════════════════════════════════════════
    // Output: the data structure sent back when user confirms
    // ═════════════════════════════════════════════════════════════════════════

    const selectionOutput = useMemo(() => {
        if (!selectedTicket || selectedSeats.length === 0) return null;
        return {
            ticket_id: selectedTicket.id,
            seats: selectedSeats.map(s => ({
                seat_id: s.seatId,
                row_id: s.rowId,
                section_id: s.sectionId,
                seat_number: s.seatNum, // canonical name (matches app toSeatPayload / backend)
                seat_num: s.seatNum,    // legacy alias, kept for older readers
                row_name: s.rowName,
                section_name: s.sectionName,
                gate: s.gate,
                zone: s.zone,
                level: s.level,
            })),
        };
    }, [selectedTicket, selectedSeats]);

    const openBrowserCheckout = useCallback((
        ticket: Ticket,
        quantity: number,
        seats: Array<{
            seat_id?: string;
            row_id?: string;
            section_id?: string;
            seat_number?: string;
            seat_num?: string;
            row_name?: string;
            section_name?: string;
            gate?: string;
            zone?: string;
            level?: string;
        }> = [],
        sectionId = '',
        sectionName = '',
    ) => {
        const token = searchParams.get('token') || getToken();
        if (!token) {
            const redirect = `${window.location.pathname}${window.location.search}`;
            window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`;
            return;
        }

        const qty = Math.max(1, quantity);
        const subtotal = ticket.price * qty;
        const platformFee = calcTicketFee(ticket.price) * qty;
        const checkoutSeats = seats.map(s => ({
            ticket_id: ticket.id,
            seat_id: s.seat_id || '',
            row_id: s.row_id || '',
            section_id: s.section_id || sectionId || '',
            seat_number: s.seat_number || s.seat_num || '',
            row_name: s.row_name || '',
            section_name: s.section_name || sectionName || '',
            gate: s.gate || '',
            zone: s.zone || '',
            level: s.level || '',
            table_name: '',
        }));

        const params = new URLSearchParams({
            token,
            tickets: JSON.stringify([{
                ticket_id: ticket.id,
                quantity: qty,
                is_standing: !!ticket.is_standing_block,
                seats: checkoutSeats,
            }]),
            amount: String(subtotal + platformFee),
            currency: ticket.currency || event?.currency || 'EUR',
            description: `${ticket.title || 'Ticket'} x${qty}`,
            promotion_id: '',
            survey_answers: '[]',
            is_donation: 'false',
            donation_message: '',
            mode: 'web',
            payment_method: '2',
            platform_fee: String(platformFee),
        });

        if (eventId) params.set('event_id', eventId);
        if (checkoutPaymentMethodType) params.set('payment_method_type', checkoutPaymentMethodType);

        window.location.href = `/checkout?${params.toString()}`;
    }, [searchParams, event?.currency, eventId, checkoutPaymentMethodType]);

    // ═════════════════════════════════════════════════════════════════════════
    // Render
    // ═════════════════════════════════════════════════════════════════════════

    const themeVars = getCssVars(isDark);

    if (loading) {
        return (
            <div style={{ ...wrapperStyle, ...themeVars } as React.CSSProperties}>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <div style={{ ...containerStyle(isLargeScreen), justifyContent: 'center', alignItems: 'center' }}>
                    <div style={spinnerStyle} />
                    <p style={{ color: 'var(--color-text-muted)', marginTop: 16 }}>Loading seat map...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ ...wrapperStyle, ...themeVars } as React.CSSProperties}>
                <div style={{ ...containerStyle(isLargeScreen), justifyContent: 'center', alignItems: 'center' }}>
                    <p style={{ color: '#e74c3c', fontSize: 16, textAlign: 'center' }}>{error}</p>
                </div>
            </div>
        );
    }

    if (!event) {
        return (
            <div style={{ ...wrapperStyle, ...themeVars } as React.CSSProperties}>
                <div style={{ ...containerStyle(isLargeScreen), justifyContent: 'center', alignItems: 'center' }}>
                    <p style={{ color: 'var(--color-text-muted)' }}>No event data available.</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ ...wrapperStyle, ...themeVars } as React.CSSProperties}>
            <div style={containerStyle(isLargeScreen)}>

                {/* ═══════════ PANEL 1: SVG Overview ═══════════ */}
                <div style={panelStyle(isLargeScreen, 1)}>
                    <div style={panelHeaderStyle}>
                        <h3 style={panelTitleStyle}>Venue Overview</h3>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <button onClick={() => {
                                overviewPZ.resetZoom();
                            }} style={smallBtnStyle} title="Reset zoom">&#x27F3;</button>
                        </div>
                    </div>
                    <div
                        ref={overviewRef}
                        onPointerUp={viewOnly ? undefined : handleOverviewPointerUp}
                        style={svgContainerStyle}
                    >
                        <SvgInner html={overviewSvgHtml} />
                        {/* Venue preview thumbnail at bottom-left */}
                        {venuePreviewUrl && (
                            <img
                                ref={venueThumbRef}
                                src={venuePreviewUrl}
                                alt="Venue interior"
                                onClick={(e) => { e.stopPropagation(); setVenuePreviewOpen(true); }}
                                style={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: 8,
                                    width: 50,
                                    height: 50,
                                    borderRadius: 6,
                                    objectFit: 'cover',
                                    cursor: 'pointer',
                                    border: '2px solid var(--color-border-mid)',
                                    zIndex: 10,
                                    transition: 'border-color 0.2s',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.borderColor = '#3498db')}
                                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-border-mid)')}
                            />
                        )}
                    </div>
                    {!svgContent && (
                        <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 20 }}>No seat map available for this event.</p>
                    )}
                    {selectedSection && (
                        <div style={sectionBadgeStyle}>
                            <span style={{ fontWeight: 600 }}>{selectedSection.name}</span>
                            {selectedSection.gate && <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}> &middot; Gate {selectedSection.gate}</span>}
                        </div>
                    )}
                </div>

                {/* ═══════════ PANEL 2: Info & Tickets ═══════════ */}
                <div style={panelStyle(isLargeScreen, 2)}>
                    <div style={panelHeaderStyle}>
                        <h3 style={panelTitleStyle}>Ticket Info</h3>
                        {selectedSectionId && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                <span style={{ color: SEAT_COLOR_BOOKED }}>{sectionBookedCount}</span>/{sectionTotalSeats} seats
                            </span>
                        )}
                    </div>
                    <div style={infoPanelContentStyle}>
                        {/* Event title */}
                        <div style={infoRowStyle}>
                            <span style={infoLabelStyle}>Event</span>
                            <span style={infoValueStyle}>{event.title}</span>
                        </div>

                        {!selectedSectionId ? (
                            <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                                <p style={{ fontSize: 32, marginBottom: 8 }}>&#9757;</p>
                                <p>Select a section on the venue map to see available tickets</p>
                            </div>
                        ) : (
                            <>
                                {/* Section info */}
                                <div style={infoRowStyle}>
                                    <span style={infoLabelStyle}>Section</span>
                                    <span style={infoValueStyle}>{selectedSection?.name || selectedSectionId}</span>
                                </div>

                                {/* Ticket selector */}
                                <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 8 }}>
                                    <span style={{ ...infoLabelStyle, display: 'block', marginBottom: 8 }}>Available Tickets</span>
                                    {ticketsForSection.length === 0 ? (
                                        <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No tickets available for this section.</p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {ticketsForSection.map(t => {
                                                const remaining = t.max_amount - t.current_amount;
                                                const isActive = selectedTicket?.id === t.id;
                                                return (
                                                    <button
                                                        key={t.id}
                                                        onClick={() => {
                                                            setSelectedTicket(t);
                                                            setSelectedSeats([]);
                                                            setTicketQuantity(1);
                                                            anchorSeatRef.current = null;
                                                        }}
                                                        style={{
                                                            padding: '10px 12px',
                                                            borderRadius: 8,
                                                            border: isActive ? '2px solid #3498db' : '1px solid var(--color-border-mid)',
                                                            background: isActive ? 'rgba(52,152,219,0.15)' : 'var(--bg-surface)',
                                                            color: 'var(--color-text)',
                                                            cursor: 'pointer',
                                                            textAlign: 'left',
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</span>
                                                            <span style={{ color: '#2ecc71', fontWeight: 700, fontSize: 14 }}>
                                                                {t.price > 0 ? `${t.price} ${t.currency}` : 'Free'}
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                                                            {remaining} remaining
                                                            {t.is_standing_block && ' \u00B7 Standing'}
                                                        </div>
                                                        {typeof selectedSection?.capacity === 'number' && selectedSection.capacity > 0 && (
                                                            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
                                                                Section capacity: {selectedSection.capacity}
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Quantity selector — shown for all ticket types */}
                                {selectedTicket && ticketRemaining > 0 && (
                                    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 12 }}>
                                        <span style={{ ...infoLabelStyle, display: 'block', marginBottom: 8 }}>
                                            {selectedTicket.is_standing_block ? 'Number of Tickets' : 'Number of Seats'}
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <button
                                                onClick={() => setTicketQuantity(q => Math.max(0, q - 1))}
                                                disabled={ticketQuantity <= 0}
                                                style={qtyBtnStyle(ticketQuantity <= 0)}
                                            >&minus;</button>
                                            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', minWidth: 30, textAlign: 'center' }}>
                                                {ticketQuantity}
                                            </span>
                                            <button
                                                onClick={() => setTicketQuantity(q => Math.min(maxQuantity, q + 1))}
                                                disabled={ticketQuantity >= maxQuantity}
                                                style={qtyBtnStyle(ticketQuantity >= maxQuantity)}
                                            >+</button>
                                            <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>max {maxQuantity}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Selected seats — view button triggers modal */}
                                {selectedSeats.length > 0 && (
                                    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 12 }}>
                                        <button
                                            onClick={() => setSelectedTicketsDropdownOpen(true)}
                                            style={{
                                                width: '100%',
                                                padding: '10px 14px',
                                                borderRadius: 8,
                                                border: '1px solid #3498db',
                                                background: 'rgba(52,152,219,0.1)',
                                                color: '#3498db',
                                                cursor: 'pointer',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            View Selected Seats ({selectedSeats.length}/{ticketQuantity})
                                        </button>
                                    </div>
                                )}

                                {/* Standing block: show compact summary instead of seats */}
                                {selectedTicket?.is_standing_block && ticketQuantity > 0 && (
                                    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 12 }}>
                                        <span style={{ ...infoLabelStyle, display: 'block', marginBottom: 8 }}>Standing Tickets</span>
                                        <p style={{ color: 'var(--color-text-sub)', fontSize: 12, margin: 0 }}>
                                            {ticketQuantity} standing {ticketQuantity === 1 ? 'ticket' : 'tickets'} in {selectedSection?.name || 'section'}
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer: Total + Confirm — pinned to bottom, always rendered to avoid flicker */}
                    {selectedTicket && selectedSectionId && (() => {
                        // Quantity at 0 means the user wants to wipe their selection.
                        // The footer turns into a "Remove Selection" confirm that posts
                        // an empty payload (quantity 0 / no seats) so the app drops this
                        // ticket — the only way to fully clear a seat-map selection.
                        const isClearing = ticketQuantity <= 0;
                        const isReady = isClearing || (!seatWarningVisible && (
                            selectedTicket.is_standing_block
                                ? ticketQuantity > 0 && selectedTicket.current_amount < selectedTicket.max_amount
                                : selectedSeats.length === ticketQuantity
                        ));
                        const totalPrice = (selectedTicket.price * (selectedTicket.is_standing_block ? ticketQuantity : selectedSeats.length)).toFixed(2);
                        return (
                            <div style={{
                                padding: '12px 16px',
                                borderTop: '1px solid var(--color-border)',
                                flexShrink: 0,
                                opacity: isReady ? 1 : 0,
                                pointerEvents: isReady ? 'auto' : 'none',
                                transition: 'opacity 0.15s',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                    {isClearing ? (
                                        <span style={{ ...infoLabelStyle, color: '#e74c3c' }}>This removes your current selection</span>
                                    ) : (
                                        <>
                                            <span style={infoLabelStyle}>Total</span>
                                            <span style={{ fontSize: 20, fontWeight: 700, color: '#2ecc71' }}>
                                                {totalPrice} {selectedTicket.currency}
                                            </span>
                                        </>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        if (!isReady) return;
                                        const output = isClearing
                                            ? {
                                                ticket_id: selectedTicket.id,
                                                ticket_title: selectedTicket.title,
                                                ticket_price: selectedTicket.price,
                                                currency: selectedTicket.currency,
                                                quantity: 0,
                                                section_id: selectedSectionId,
                                                section_name: selectedSection?.name || '',
                                                is_standing_block: !!selectedTicket.is_standing_block,
                                                seats: [],
                                            }
                                            : selectedTicket.is_standing_block
                                            ? {
                                                ticket_id: selectedTicket.id,
                                                ticket_title: selectedTicket.title,
                                                ticket_price: selectedTicket.price,
                                                currency: selectedTicket.currency,
                                                quantity: ticketQuantity,
                                                section_id: selectedSectionId,
                                                section_name: selectedSection?.name || '',
                                                is_standing_block: true,
                                                seats: [],
                                            }
                                            : {
                                                ...selectionOutput,
                                                ticket_title: selectedTicket.title,
                                                ticket_price: selectedTicket.price,
                                                currency: selectedTicket.currency,
                                                quantity: selectedSeats.length,
                                                section_id: selectedSectionId,
                                                section_name: selectedSection?.name || '',
                                                is_standing_block: false,
                                            };
                                        const message = { type: 'seat-selection-confirmed', data: output };
                                        // React Native WebView
                                        if ((window as any).ReactNativeWebView?.postMessage) {
                                            (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
                                        }
                                        // iframe / parent window
                                        window.parent.postMessage(message, '*');
                                        if (browserCheckoutMode && !(window as any).ReactNativeWebView) {
                                            openBrowserCheckout(
                                                selectedTicket,
                                                output.quantity,
                                                output.seats || [],
                                                output.section_id,
                                                output.section_name,
                                            );
                                        }
                                    }}
                                    style={{ ...confirmBtnStyle, marginTop: 0, ...(isClearing ? { background: '#e74c3c' } : null) }}
                                >
                                    {isClearing ? 'Remove Selection' : 'Confirm Selection'}
                                </button>
                            </div>
                        );
                    })()}
                </div>

                {/* ═══════════ PANEL 3: Section Detail ═══════════ */}
                <div style={{ ...panelStyle(isLargeScreen, 3), position: 'relative' }}>
                    <div style={panelHeaderStyle}>
                        <h3 style={panelTitleStyle}>
                            {selectedSection ? `${selectedSection.name} \u2013 ${selectedTicket?.is_standing_block ? 'Standing Area' : 'Seats'}` : 'Section Detail'}
                        </h3>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {selectedSection && !selectedTicket?.is_standing_block && (
                                <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{selectedSeats.length}/{ticketQuantity} selected</span>
                            )}
                            {selectedSectionId && (
                                <button onClick={() => {
                                    setSelectedSeats([]);
                                    setTicketQuantity(1);
                                    setSelectedTicketsDropdownOpen(false);
                                    anchorSeatRef.current = null;
                                    // Re-zoom to section bounds
                                    const el = detailRef.current;
                                    if (el && selectedSectionId) {
                                        const svg = el.querySelector('svg');
                                        if (svg) {
                                            const bbox = getSectionBBox(selectedSectionId, svg);
                                            if (bbox) detailPZ.zoomToRect(bbox, 30);
                                        }
                                    }
                                }} style={smallBtnStyle} title="Clear selection">&#x27F3;</button>
                            )}
                        </div>
                    </div>
                    {!selectedSectionId ? (
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'var(--color-text-muted)', padding: 20, textAlign: 'center' }}>
                            <p>Select a section from the overview to see individual seats</p>
                        </div>
                    ) : selectedTicket?.is_standing_block ? (
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', color: 'var(--color-text-muted)', padding: 20, textAlign: 'center' }}>
                            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>&#127918;</div>
                            <p style={{ fontSize: 14, color: 'var(--color-text-sub)', margin: 0 }}>Standing Area</p>
                            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
                                No specific seat selection needed. Use the quantity selector to choose how many tickets you want.
                            </p>
                        </div>
                    ) : (
                        <div
                            ref={detailRef}
                            onClick={viewOnly ? undefined : handleDetailSeatClick}
                            style={svgContainerStyle}
                        >
                            <SvgInner html={detailSvgHtml} />
                        </div>
                    )}
                    {/* Seat warning popup — only covers panel 3 */}
                    {seatWarningVisible && (
                        <div
                            onClick={() => setSeatWarningVisible(false)}
                            style={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                background: 'rgba(0,0,0,0.3)',
                                zIndex: 20,
                                borderRadius: 'inherit',
                            }}
                        >
                            <div
                                onClick={e => e.stopPropagation()}
                                style={{
                                    background: '#fff',
                                    color: '#e74c3c',
                                    padding: '16px 24px',
                                    borderRadius: 12,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                                    textAlign: 'center',
                                    maxWidth: 280,
                                }}
                            >
                                Please don't leave single seats
                            </div>
                        </div>
                    )}
                    {/* Seat legend */}
                    {!selectedTicket?.is_standing_block && (
                        <div style={legendStyle}>
                            <div style={legendItemStyle}><span style={{ ...legendDotStyle, background: SEAT_COLOR_AVAILABLE }} /> Available</div>
                            <div style={legendItemStyle}><span style={{ ...legendDotStyle, background: SEAT_COLOR_SELECTED }} /> Selected</div>
                            <div style={legendItemStyle}><span style={{ ...legendDotStyle, background: SEAT_COLOR_BOOKED }} /> Booked</div>
                        </div>
                    )}
                </div>
            </div>

            {/* ═══════════ Venue Interior Preview Modal ═══════════ */}
            {venuePreviewOpen && venuePreviewUrl && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.96)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2000,
                    }}
                    onClick={() => setVenuePreviewOpen(false)}
                >
                    <button
                        onClick={e => { e.stopPropagation(); setVenuePreviewOpen(false); }}
                        style={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            background: 'rgba(255,255,255,0.15)',
                            backdropFilter: 'blur(4px)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '50%',
                            color: '#fff',
                            width: 40,
                            height: 40,
                            cursor: 'pointer',
                            fontSize: 18,
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                        }}
                    >&#10005;</button>
                    {venuePreviewUrl.match(/\.(mp4|webm)$/i) ? (
                        <video
                            src={venuePreviewUrl}
                            controls
                            onClick={e => e.stopPropagation()}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <img
                            src={venuePreviewUrl}
                            alt="Venue interior"
                            onClick={e => e.stopPropagation()}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    )}
                </div>
            )}
            {/* ═══════════ Selected Seats Modal ═══════════ */}
            {selectedTicketsDropdownOpen && selectedSeats.length > 0 && (
                <div style={modalOverlayStyle} onClick={() => setSelectedTicketsDropdownOpen(false)}>
                    <div onClick={e => e.stopPropagation()} style={{ ...modalContentStyle, maxWidth: 500, width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h3 style={{ margin: 0, color: 'var(--color-text)', fontSize: 16 }}>
                                Selected Seats ({selectedSeats.length}/{ticketQuantity})
                            </h3>
                            <button onClick={() => setSelectedTicketsDropdownOpen(false)} style={modalCloseBtnStyle}>&#10005;</button>
                        </div>
                        <div style={{ overflowY: 'auto', maxHeight: '60vh' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--color-border-mid)' }}>
                                        <th style={thStyle}>Gate</th>
                                        <th style={thStyle}>Zone</th>
                                        <th style={thStyle}>Level</th>
                                        <th style={thStyle}>Section</th>
                                        <th style={thStyle}>Row</th>
                                        <th style={thStyle}>Seat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedSeats.map(s => (
                                        <tr key={s.seatId} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                            <td style={tdStyle}>{s.gate || '-'}</td>
                                            <td style={tdStyle}>{s.zone || '-'}</td>
                                            <td style={tdStyle}>{s.level || '-'}</td>
                                            <td style={tdStyle}>{s.sectionName}</td>
                                            <td style={tdStyle}>{s.rowName}</td>
                                            <td style={tdStyle}>{s.seatNum}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════════

const wrapperStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'var(--bg-page)',
    padding: 10,
    boxSizing: 'border-box',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    overflow: 'hidden',
    maxWidth: '100vw',
};

const containerStyle = (isLarge: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: isLarge ? 'row' : 'column',
    width: '100%',
    maxWidth: isLarge ? 1400 : '100%',
    height: isLarge ? 'calc(100vh - 20px)' : 'auto',
    maxHeight: isLarge ? 'calc(100vh - 20px)' : 'none',
    background: 'var(--bg-container)',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: 'var(--shadow)',
    boxSizing: 'border-box',
});

const panelStyle = (isLarge: boolean, panelIndex: number): React.CSSProperties => {
    const flexMap: Record<number, number> = { 1: 2, 2: 1, 3: 2 };
    return {
        flex: isLarge ? flexMap[panelIndex] : 'none',
        display: 'flex',
        flexDirection: 'column',
        minHeight: isLarge ? 0 : (panelIndex === 2 ? 'auto' : 300),
        height: isLarge ? '100%' : (panelIndex === 2 ? 'auto' : 350),
        borderRight: isLarge && panelIndex < 3 ? '1px solid var(--color-border)' : 'none',
        borderBottom: !isLarge && panelIndex < 3 ? '1px solid var(--color-border)' : 'none',
        overflow: 'hidden',
        minWidth: 0,
        boxSizing: 'border-box',
        width: isLarge ? undefined : '100%',
    };
};

const panelHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-border)',
    flexShrink: 0,
};

const panelTitleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-text)',
};

const svgContainerStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'hidden',
    background: 'var(--bg-svg)',
    touchAction: 'none',
    cursor: 'grab',
    position: 'relative',
};

const infoPanelContentStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'auto',
    padding: '12px 16px',
    boxSizing: 'border-box',
    wordBreak: 'break-word',
};

const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
};

const infoLabelStyle: React.CSSProperties = {
    fontSize: 12,
    color: 'var(--color-text-muted)',
    fontWeight: 500,
};

const infoValueStyle: React.CSSProperties = {
    fontSize: 13,
    color: 'var(--color-text)',
    fontWeight: 600,
};

const smallBtnStyle: React.CSSProperties = {
    background: 'none',
    border: '1px solid var(--color-border-mid)',
    borderRadius: 6,
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: 14,
    color: 'var(--color-text)',
};

const qtyBtnStyle = (disabled: boolean): React.CSSProperties => ({
    width: 36,
    height: 36,
    borderRadius: 8,
    border: 'none',
    background: disabled ? 'var(--bg-surface)' : '#3498db',
    color: disabled ? 'var(--color-text-faint)' : '#fff',
    fontSize: 18,
    fontWeight: 700,
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const sectionBadgeStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderTop: '1px solid var(--color-border)',
    background: 'var(--bg-surface)',
    fontSize: 13,
    color: 'var(--color-text)',
    textAlign: 'center',
    flexShrink: 0,
};

const confirmBtnStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px',
    marginTop: 16,
    borderRadius: 10,
    border: 'none',
    background: '#27ae60',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 0.2s',
};

const legendStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
    padding: '8px 16px',
    borderTop: '1px solid var(--color-border)',
    flexShrink: 0,
};

const legendItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: 'var(--color-text-muted)',
};

const legendDotStyle: React.CSSProperties = {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
};

const spinnerStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    border: '3px solid var(--color-spinner-track)',
    borderTop: '3px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
};

const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0, left: 0,
    width: '100vw', height: '100vh',
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
    padding: 16,
    boxSizing: 'border-box',
};

const modalContentStyle: React.CSSProperties = {
    background: 'var(--bg-container)',
    borderRadius: 12,
    padding: 20,
    maxWidth: 700,
    maxHeight: '90vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
};

const modalCloseBtnStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    border: 'none',
    borderRadius: 6,
    color: 'var(--color-text)',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: 14,
};

const thStyle: React.CSSProperties = {
    padding: '4px 6px',
    textAlign: 'left',
    color: 'var(--color-text-muted)',
    fontWeight: 600,
    fontSize: 10,
    textTransform: 'uppercase',
};

const tdStyle: React.CSSProperties = {
    padding: '4px 6px',
    color: 'var(--color-text-sub)',
    fontSize: 11,
};
export default SeatSelectionComponent;
